import { CartT } from '@lambda/apis/src/cart/types';
import { SkuT } from '@lambda/apis/src/catalog-v2/types';
import { trackEvent } from './vendor/segment/segment';
import { SegmentEvent } from './events';
import {
  CartEventProperties,
  CartTrackRequest,
  ProductEventProperties,
  ProductSource,
} from './types';
import { makeProductProperties } from './trackProduct';
import settings from '@/settings';
import { logger } from '../logger';

const makeCartProperties = (cart: CartT): CartEventProperties => ({
  store: settings.store,
  cart_id: cart.id,
  products: cart.lines.map((lineItem) =>
    makeProductProperties({
      cartLine: lineItem,
    }),
  ),
});

const trackCartEvent = (event: SegmentEvent, r: CartTrackRequest): void => {
  try {
    logger.log({ event, r }, 'tracking cart event');

    const properties = { ...makeCartProperties(r.cart), ...r.properties };

    trackEvent(event, properties);
  } catch (e: any) {
    logger.error({ event, r }, 'error tracking cart event');
  }
};

export const trackCartViewed = (cart: CartT) =>
  trackCartEvent(SegmentEvent.CART_VIEWED, { cart });

export const trackCartUpdateSimilarOffersClicked = (r: CartTrackRequest) =>
  trackCartEvent(SegmentEvent.CART_UPDATE_SIMILAR_OFFERS_CLICKED, r);

export const trackCartUpdateModalDismissed = (r: CartTrackRequest) =>
  trackCartEvent(SegmentEvent.CART_UPDATE_MODAL_DISMISSED, r);

/** Recommendations rendered against a single cart line, keyed by line item id. */
export type RecommendationsByLineId = Record<string, SkuT[]>;

const makeRecommendationsProperties = (
  recommendations: SkuT[],
): ProductEventProperties[] =>
  recommendations.flatMap((sku, index) => {
    const offer = sku?.offers?.[0];
    const product = sku?.product;

    // Mirrors the render guard in FrequentlyBoughtTogether — a recommendation
    // missing its offer or product is not rendered, but still consumes a slot,
    // so slot_number is derived before this filter.
    if (!sku || !offer || !product) {
      return [];
    }

    return [
      makeProductProperties({
        v2: {
          sku,
          product,
          offer,
          isOutOfStock: false,
          slotNumber: index + 1,
        },
      }),
    ];
  });

/**
 * Cart redesign only. Fires once per cart view, after every line item's
 * recommendation slot has resolved. Each product carries the recommendations
 * rendered against it — an empty array means the slot resolved with nothing
 * to show, which is what makes recommendation coverage measurable.
 */
export const trackRecommendationsViewed = (
  cart: CartT,
  recommendationsByLineId: RecommendationsByLineId,
  /**
   * Where they were shown, when not the cart. A page that shows them for one
   * device (the purchase flow's accessories page) reports only that device's
   * line: the cart's other lines weren't offered anything there, which an
   * empty array would misreport as "nothing to show".
   */
  source?: ProductSource,
) =>
  trackCartEvent(SegmentEvent.CART_RECOMMENDATIONS_VIEWED, {
    cart,
    properties: {
      store: settings.store,
      cart_id: cart.id,
      ...(source ? { source } : {}),
      products: cart.lines
        .filter((lineItem) => !source || lineItem.id in recommendationsByLineId)
        .map((lineItem) => ({
          ...makeProductProperties({ cartLine: lineItem }),
          recommendations: makeRecommendationsProperties(
            recommendationsByLineId[lineItem.id] ?? [],
          ),
        })),
    },
  });
